import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../../services/api';
import { ActivityEventType } from '@bst-stellar/shared';
import { formatDate } from '../../utils/dateFormatter';

interface AdminStats {
    totalGames: number;
    activeGames: number;
    completedGames: number;
    pendingClaims: number;
    totalEmployees: number;
    activeEmployees: number;
}

interface ActivityEvent {
    id: string;
    eventType: string;
    eventData: Record<string, any>;
    createdAt: string;
}

export default function AdminDashboard() {
    const api = useApi();
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [activity, setActivity] = useState<ActivityEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [activityLoading, setActivityLoading] = useState(true);

    useEffect(() => {
        loadStats();
        loadActivity();
    }, []);

    async function loadStats() {
        try {
            setLoading(true);
            const result = await api.getStats();
            setStats(result.stats);
        } catch (error) {
            console.error('Failed to load stats:', error);
        } finally {
            setLoading(false);
        }
    }

    async function loadActivity() {
        try {
            setActivityLoading(true);
            const result = await api.getActivity({ pageSize: 10 });
            setActivity(result.events || []);
        } catch (error) {
            console.error('Failed to load activity:', error);
        } finally {
            setActivityLoading(false);
        }
    }

    if (loading) {
        return <div className="text-center py-12 text-text-secondary">Loading...</div>;
    }

    return (
        <div>
            <h1 className="text-3xl font-bold mb-6 text-text-primary">Admin Dashboard</h1>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <StatCard
                    title="Active Games"
                    value={stats?.activeGames || 0}
                    subtitle={`${stats?.totalGames || 0} total`}
                    icon="🎮"
                    color="blue"
                />
                <StatCard
                    title="Pending Claims"
                    value={stats?.pendingClaims || 0}
                    subtitle="Awaiting validation"
                    icon="⏱️"
                    color="yellow"
                />
                <StatCard
                    title="Active Employees"
                    value={stats?.activeEmployees || 0}
                    subtitle={`${stats?.totalEmployees || 0} total`}
                    icon="👥"
                    color="green"
                />
            </div>

            {/* Quick Actions */}
            <div className="bg-surface rounded-lg shadow p-6 mb-8 border border-border">
                <h2 className="text-xl font-bold mb-4 text-text-primary">Quick Actions</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <QuickAction
                        to="/admin/games/new"
                        icon="➕"
                        label="Create Game"
                        description="Start a new bingo game"
                    />
                    <QuickAction
                        to="/admin/claims"
                        icon="✅"
                        label="Validate Claims"
                        description={`${stats?.pendingClaims || 0} pending`}
                    />
                    <QuickAction
                        to="/admin/employees"
                        icon="👤"
                        label="Add Employee"
                        description="Invite new team member"
                    />
                    <QuickAction
                        to="/admin/items"
                        icon="📝"
                        label="Create Item"
                        description="Add custom bingo item"
                    />
                </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-surface rounded-lg shadow p-6 border border-border">
                <h2 className="text-xl font-bold mb-4 text-text-primary">Recent Activity</h2>
                {activityLoading ? (
                    <p className="text-text-secondary">Loading activity...</p>
                ) : activity.length === 0 ? (
                    <p className="text-text-secondary">No recent activity</p>
                ) : (
                    <div className="space-y-3">
                        {activity.map((event) => (
                            <ActivityItem key={event.id} event={event} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function ActivityItem({ event }: { event: ActivityEvent }) {
    const getEventDisplay = () => {
        const data = event.eventData;
        switch (event.eventType) {
            case ActivityEventType.CLAIM_CREATED:
                return {
                    icon: '📝',
                    color: 'text-blue-400',
                    message: `${data.userName || 'Someone'} submitted a claim for "${data.itemTitle || 'an item'}"`,
                };
            case ActivityEventType.CLAIM_APPROVED:
                return {
                    icon: '✅',
                    color: 'text-green-400',
                    message: `Claim approved for "${data.itemTitle || 'an item'}"`,
                };
            case ActivityEventType.CLAIM_REJECTED:
                return {
                    icon: '❌',
                    color: 'text-red-400',
                    message: `Claim rejected for "${data.itemTitle || 'an item'}"${data.reason ? `: ${data.reason}` : ''}`,
                };
            case ActivityEventType.BINGO_LINE:
                return {
                    icon: '🎉',
                    color: 'text-purple-400',
                    message: `${data.userName || 'Someone'} achieved a bingo line!`,
                };
            case ActivityEventType.GAME_STARTED:
                return {
                    icon: '🎮',
                    color: 'text-primary',
                    message: `Game "${data.gameName || 'Unnamed'}" started`,
                };
            case ActivityEventType.GAME_ENDED:
                return {
                    icon: '🏁',
                    color: 'text-yellow-400',
                    message: `Game "${data.gameName || 'Unnamed'}" ended`,
                };
            case ActivityEventType.REVEAL_STARTED:
                return {
                    icon: '👁️',
                    color: 'text-cyan-400',
                    message: `Reveal started for "${data.gameName || 'a game'}"`,
                };
            default:
                return {
                    icon: '📌',
                    color: 'text-text-secondary',
                    message: `Activity: ${event.eventType}`,
                };
        }
    };

    const display = getEventDisplay();
    const timeAgo = formatTimeAgo(new Date(event.createdAt));

    return (
        <div className="flex items-start gap-3 p-3 bg-background rounded-lg border border-border">
            <span className={`text-xl ${display.color}`}>{display.icon}</span>
            <div className="flex-1 min-w-0">
                <p className="text-text-primary text-sm">{display.message}</p>
                <p className="text-text-muted text-xs mt-1">{timeAgo}</p>
            </div>
        </div>
    );
}

function formatTimeAgo(date: Date): string {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return formatDate(date);
}

interface StatCardProps {
    title: string;
    value: number;
    subtitle: string;
    icon: string;
    color: 'blue' | 'yellow' | 'green';
}

function StatCard({ title, value, subtitle, icon, color }: StatCardProps) {
    const colorClasses = {
        blue: 'bg-blue-500/10 text-blue-400',
        yellow: 'bg-yellow-500/10 text-yellow-400',
        green: 'bg-green-500/10 text-green-400',
    };

    return (
        <div className="bg-surface rounded-lg shadow p-6 border border-border">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-text-secondary text-sm font-medium">{title}</h3>
                <span className="text-2xl">{icon}</span>
            </div>
            <div className={`text-3xl font-bold inline-block px-3 py-1 rounded-lg ${colorClasses[color]}`}>
                {value}
            </div>
            <p className="text-text-secondary text-sm mt-2">{subtitle}</p>
        </div>
    );
}

interface QuickActionProps {
    to: string;
    icon: string;
    label: string;
    description: string;
}

function QuickAction({ to, icon, label, description }: QuickActionProps) {
    return (
        <Link
            to={to}
            className="block p-4 border-2 border-border rounded-lg hover:border-primary hover:shadow-md transition bg-surface"
        >
            <div className="text-3xl mb-2">{icon}</div>
            <h3 className="font-semibold mb-1 text-text-primary">{label}</h3>
            <p className="text-sm text-text-secondary">{description}</p>
        </Link>
    );
}
